import { CancelOutlined, CheckCircle } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { getDeviceDeploymentSessions } from 'lib/firebase';
import { useEffect, useState } from 'react';
import { Device, DeviceDeploymentData } from 'types';
import styles from './DeviceDeployment.module.scss';

interface DeviceDeploymentProps {
    device: Device;
}

export const DeviceDeployment = ({ device }: DeviceDeploymentProps) => {
    const [deploymentList, setDeploymentList] = useState<
        DeviceDeploymentData[]
    >([]);

    useEffect(() => {
        if (
            device?.deviceId &&
            (device?.protected?.supplierId ||
                device?.protected?.supplierId?.toString() === '0')
        ) {
            getDeviceDeploymentSessions(
                device.deviceId,
                device.protected.supplierId
            ).then((result) => {
                if (result) {
                    const deployList = Object.keys(result).map((id) => ({
                        ...result[id],
                        id,
                    }));
                    if (deployList.length) {
                        setDeploymentList(
                            deployList.filter((deployment) => !!deployment)
                        );
                    } else {
                        setDeploymentList([]);
                    }
                } else {
                    setDeploymentList([]);
                }
            });
        }
    }, [device]);

    const columns: GridColDef[] = [
        {
            field: 'sitePicture.url',
            headerName: 'Gauge Picture',
            flex: 2,
            renderCell: (params) => <ImageCell {...params} />,
            sortable: false,
        },
        {
            field: 'address',
            headerName: 'Address',
            flex: 3,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'date',
            headerName: 'Date',
            flex: 1,
            valueGetter: (params) =>
                params.row?.date
                    ? format(new Date(params.row.date), 'MM/dd/yy')
                    : '-',
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'index',
            headerName: 'Page Reached',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'cloneInfo',
            headerName: 'Clone Info',
            flex: 2,
            sortingOrder: ['asc', 'desc'],
            renderCell: (params) =>
                params.row?.cloneInfo ? (
                    <ul>
                        <li>Source: {params.row.cloneInfo?.source}</li>
                        <li>
                            Destination: {params.row.cloneInfo?.destination}
                        </li>
                    </ul>
                ) : (
                    <div>N/A</div>
                ),
        },
        {
            field: 'result',
            headerName: 'Success',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
            renderCell: (params) =>
                params.row?.result ? <CheckCircle /> : <CancelOutlined />,
        },
    ];

    return (
        <div data-testid="DeviceDeployment" className={styles.container}>
            <DataGrid
                columns={columns}
                rows={deploymentList}
                disableSelectionOnClick
                getRowHeight={() => 'auto'}
                getCellClassName={() => styles.deployCell}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'date', sort: 'desc' }],
                    },
                }}
                disableColumnMenu
            />
        </div>
    );
};

const ImageCell = (params: GridRenderCellParams) => {
    const url = params.row?.sitePicture?.url;

    if (!url) {
        return <div>N/A</div>;
    }

    return (
        <img
            onClick={() => window.open(url, '_blank')}
            className={styles.gaugeImage}
            src={url}
            alt={url}
        />
    );
};
